import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  FileText, Plus, Edit, Trash2, FolderOpen, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'hr', label: 'HR / Employment' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'care', label: 'Care Documentation' },
  { value: 'policy', label: 'Policies' },
  { value: 'general', label: 'General' },
];

export default function DocumentTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'general', content: '' });
  const [filterCategory, setFilterCategory] = useState('all');
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/documents/templates`, { withCredentials: true });
      setTemplates(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      if (editingTemplate) {
        await axios.put(`${API_URL}/api/documents/templates/${editingTemplate.id}`, form, { withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/api/documents/templates`, form, { withCredentials: true });
        toast.success('Template created');
      }
      setDialogOpen(false);
      setEditingTemplate(null);
      setForm({ name: '', category: 'general', content: '' });
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setForm({ name: template.name, category: template.category, content: template.content });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/templates/${id}`, { withCredentials: true });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const filtered = filterCategory === 'all' ? templates : templates.filter(t => t.category === filterCategory);

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="document-templates-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <FileText className="w-7 h-7 inline mr-3 text-[#A8E6CF]" />
              Document Templates
            </h1>
            <p className="text-muted-foreground mt-1">Manage HR, compliance, and care document templates</p>
          </div>
          {isAdmin && (
            <Button className="btn-primary" onClick={() => { setEditingTemplate(null); setForm({ name: '', category: 'general', content: '' }); setDialogOpen(true); }}
              data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />New Template
            </Button>
          )}
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 flex-wrap">
          <Button variant={filterCategory === 'all' ? 'default' : 'outline'} size="sm"
            className={filterCategory === 'all' ? 'bg-[#00A6A0] text-[#0F1419]' : 'border-border text-muted-foreground'}
            onClick={() => setFilterCategory('all')} data-testid="filter-all">All</Button>
          {CATEGORIES.map(c => (
            <Button key={c.value} variant={filterCategory === c.value ? 'default' : 'outline'} size="sm"
              className={filterCategory === c.value ? 'bg-[#00A6A0] text-[#0F1419]' : 'border-border text-muted-foreground'}
              onClick={() => setFilterCategory(c.value)} data-testid={`filter-${c.value}`}>
              {c.label}
            </Button>
          ))}
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No templates found</p>
            {isAdmin && <p className="text-xs text-muted-foreground mt-2">Create your first template to get started</p>}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(template => (
              <Card key={template.id} className="glass-card border-border hover:border-[#00A6A0]/30 transition-colors"
                data-testid={`template-card-${template.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#A8E6CF]/10">
                        <FileText className="w-5 h-5 text-[#A8E6CF]" />
                      </div>
                      <div>
                        <h3 className="text-foreground font-medium text-sm">{template.name}</h3>
                        <span className="text-xs text-muted-foreground capitalize">{template.category}</span>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          onClick={() => handleEdit(template)} data-testid={`edit-template-${template.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-400"
                          onClick={() => handleDelete(template.id)} data-testid={`delete-template-${template.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-3">{template.content || 'No content preview'}</p>
                  <div className="mt-3 text-[10px] text-muted-foreground">
                    Updated: {new Date(template.updated_at).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="glass-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-heading text-foreground">
                {editingTemplate ? 'Edit Template' : 'New Document Template'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Template Name</label>
                <Input value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))}
                  placeholder="e.g., Employee Onboarding Checklist"
                  className="bg-muted border-border text-foreground"
                  data-testid="template-name-input" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm(p => ({...p, category: v}))}>
                  <SelectTrigger className="bg-muted border-border text-foreground" data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass-card border-border">
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Content</label>
                <textarea value={form.content}
                  onChange={(e) => setForm(p => ({...p, content: e.target.value}))}
                  placeholder="Template content..."
                  className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground placeholder:text-muted-foreground text-sm min-h-[200px] focus:ring-1 focus:ring-[#00A6A0] focus:outline-none"
                  data-testid="template-content-input" />
              </div>
              <Button className="w-full btn-primary" onClick={handleSave} data-testid="save-template-btn">
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
